import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileSearch, FileText, ClipboardList, FileCheck2, Cylinder, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Commercial Chain — visual breadcrumb of the linked flow:
 *   Inquiry → Quotation → Customer PO → Order Acceptance → Cylinders
 *
 * Pass the current entity's data (`current`) and its `type` — the component
 * auto-resolves any missing links by traversing the reference IDs.
 */
const STAGES = [
  { key: "inquiry",   label: "Inquiry",       icon: FileSearch,   route: "/inquiries" },
  { key: "quotation", label: "Quotation",     icon: FileText,     route: "/quotations" },
  { key: "po",        label: "Customer PO",   icon: ClipboardList,route: "/purchase-orders" },
  { key: "oa",        label: "Order Acceptance", icon: FileCheck2, route: "/order-acceptances" },
  { key: "cylinders", label: "Cylinders",     icon: Cylinder,     route: "/cylinders" },
];

export default function CommercialChain({ type, entity }) {
  const [nodes, setNodes] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      setLoading(true);
      const n = {};

      // ---------- Seed the chain with the current entity ----------
      if (type === "inquiry") {
        n.inquiry = { id: entity.id, number: entity.inquiry_number };
        const q = (entity.quotations || [])[0];
        if (q) n.quotation = { id: q.id, number: q.display_number || q.quotation_number };
      } else if (type === "quotation") {
        n.quotation = { id: entity.id, number: entity.display_number || entity.quotation_number };
        if (entity.inquiry_id) n.inquiry = { id: entity.inquiry_id, number: entity.inquiry_number };
        const po = (entity.purchase_orders || [])[0];
        if (po) n.po = { id: po.id, number: po.po_number };
      } else if (type === "po") {
        n.po = { id: entity.id, number: entity.po_number };
        if (entity.quotation_id) n.quotation = { id: entity.quotation_id, number: entity.quotation_number };
        if (entity.inquiry_id) n.inquiry = { id: entity.inquiry_id, number: entity.inquiry_number };
        const oa = (entity.order_acceptances || [])[0];
        if (oa) n.oa = { id: oa.id, number: oa.oa_number };
      } else if (type === "oa") {
        n.oa = { id: entity.id, number: entity.oa_number };
        if (entity.purchase_order_id) n.po = { id: entity.purchase_order_id, number: entity.po_number };
        if (entity.quotation_id) n.quotation = { id: entity.quotation_id, number: entity.quotation_number };
        if (entity.inquiry_id) n.inquiry = { id: entity.inquiry_id, number: entity.inquiry_number };
      } else if (type === "cylinder") {
        n.cylinders = { count: 1, first: { id: entity.id, number: entity.cylinder_number } };
        if (entity.order_acceptance_id) n.oa = { id: entity.order_acceptance_id, number: entity.oa_number };
        if (entity.purchase_order_id) n.po = { id: entity.purchase_order_id, number: entity.po_number };
        if (entity.quotation_id) n.quotation = { id: entity.quotation_id, number: entity.quotation_number };
        if (entity.inquiry_id) n.inquiry = { id: entity.inquiry_id, number: entity.inquiry_number };
      }

      // ---------- Fetch missing upstream/downstream entities by ID ----------
      try {
        if (n.inquiry?.id && !n.inquiry.number) {
          const r = await api.get(`/inquiries/${n.inquiry.id}`);
          n.inquiry.number = r.data?.inquiry_number || n.inquiry.number;
        }
        if (n.quotation?.id && !n.quotation.number) {
          const r = await api.get(`/quotations/${n.quotation.id}`);
          n.quotation.number = r.data?.display_number || r.data?.quotation_number || n.quotation.number;
        }
        if (n.po?.id && !n.po.number) {
          const r = await api.get(`/purchase-orders-detail/${n.po.id}`);
          n.po.number = r.data?.po_number || n.po.number;
        }
        if (n.oa?.id && !n.oa.number) {
          const r = await api.get(`/order-acceptances/${n.oa.id}`);
          n.oa.number = r.data?.oa_number || n.oa.number;
        }
      } catch { /* silent — chain nodes gracefully render "—" if fetch fails */ }

      // ---------- Fetch downstream cylinders count (if we have an OA) ----------
      try {
        if (n.oa && !n.cylinders) {
          const res = await api.get("/cylinders", { params: { order_acceptance_id: n.oa.id, limit: 200 } });
          const list = Array.isArray(res.data) ? res.data : (res.data.items || []);
          const linked = list.filter((c) => c.order_acceptance_id === n.oa.id);
          if (linked.length) {
            n.cylinders = { count: linked.length, first: { id: linked[0].id, number: linked[0].cylinder_number } };
          }
        }
      } catch { /* silent */ }

      if (!cancelled) { setNodes(n); setLoading(false); }
    }
    if (entity) resolve();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, entity?.id]);

  if (loading) {
    return (
      <div data-testid="commercial-chain" className="flex items-center gap-2 text-xs text-slate-500 py-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Resolving commercial chain…
      </div>
    );
  }

  return (
    <div data-testid="commercial-chain" className="flex flex-wrap items-stretch gap-2">
      {STAGES.map((stage, idx) => {
        const node = nodes[stage.key];
        const isCurrent = stage.key === type || (stage.key === "cylinders" && type === "cylinder");
        const Icon = stage.icon;
        return (
          <React.Fragment key={stage.key}>
            {idx > 0 && (
              <div className="flex items-center text-slate-300 select-none">→</div>
            )}
            <ChainNode
              stage={stage}
              node={node}
              isCurrent={isCurrent}
              Icon={Icon}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ChainNode({ stage, node, isCurrent, Icon }) {
  const base = "flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs transition-colors";
  if (!node) {
    return (
      <div
        data-testid={`chain-${stage.key}-empty`}
        className={`${base} border-dashed border-slate-200 bg-slate-50 text-slate-400`}
      >
        <Icon className="w-3.5 h-3.5" />
        <div className="leading-tight">
          <div className="text-[9px] uppercase tracking-wider font-semibold">{stage.label}</div>
          <div className="font-mono text-[11px]">—</div>
        </div>
      </div>
    );
  }
  const isCylinders = stage.key === "cylinders";
  const label = isCylinders
    ? (node.count > 1 ? `${node.count} cylinders` : node.first?.number || "—")
    : node.number;
  const to = isCylinders
    ? (node.first?.id ? `${stage.route}/${node.first.id}` : stage.route)
    : `${stage.route}/${node.id}`;

  const cls = isCurrent
    ? `${base} border-red-300 bg-red-50 text-red-900 font-semibold shadow-sm`
    : `${base} border-slate-200 bg-white text-slate-700 hover:border-blue-400 hover:bg-blue-50/40`;

  return (
    <Link
      to={to}
      data-testid={`chain-${stage.key}`}
      className={cls}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <div className="leading-tight">
        <div className="text-[9px] uppercase tracking-wider font-semibold opacity-70">{stage.label}</div>
        <div className="font-mono text-[11px] font-semibold">{label}</div>
      </div>
    </Link>
  );
}
